import React from 'react'

import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'

import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import {
	Pagination,
	PaginationContent,
	PaginationEllipsis,
	PaginationItem,
	PaginationLink,
} from '@/shared/components/atoms/Pagination'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/shared/components/atoms/Select'

interface PaginationControlsProps {
	currentPage: number
	totalPages: number
	onPageChange: (page: number) => void
	className?: string
	pageSize?: number
	onPageSizeChange?: (pageSize: number) => void
}

export const PaginationControls: React.FC<PaginationControlsProps> = ({
	currentPage,
	totalPages,
	onPageChange,
	className = '',
	pageSize,
	onPageSizeChange,
}) => {
	const { t } = useTranslation('common')
	const getPageNumbers = () => {
		const pages: (number | 'ellipsis')[] = []

		if (totalPages <= 7) {
			for (let i = 1; i <= totalPages; i++) {
				pages.push(i)
			}
		} else {
			if (currentPage <= 3) {
				for (let i = 1; i <= 4; i++) {
					pages.push(i)
				}
				pages.push('ellipsis')
				pages.push(totalPages)
			} else if (currentPage >= totalPages - 2) {
				pages.push(1)
				pages.push('ellipsis')
				for (let i = totalPages - 3; i <= totalPages; i++) {
					pages.push(i)
				}
			} else {
				pages.push(1)
				pages.push('ellipsis')
				for (let i = currentPage - 1; i <= currentPage + 1; i++) {
					pages.push(i)
				}
				pages.push('ellipsis')
				pages.push(totalPages)
			}
		}

		return pages
	}

	const handlePrevious = (e: React.MouseEvent) => {
		e.preventDefault()
		if (currentPage > 1) {
			onPageChange(currentPage - 1)
		}
	}

	const handleNext = (e: React.MouseEvent) => {
		e.preventDefault()
		if (currentPage < totalPages) {
			onPageChange(currentPage + 1)
		}
	}

	const handlePageClick = (e: React.MouseEvent, page: number) => {
		e.preventDefault()
		onPageChange(page)
	}

	if (totalPages <= 1) {
		return null
	}

	const pageNumbers = getPageNumbers()

	return (
		<div
			className={`flex items-center justify-start gap-2 md:gap-6 ${className}`}
		>
			<div>
				<Pagination className="mx-0 w-auto">
					<PaginationContent>
						<PaginationItem>
							<Button
								variant="ghost"
								size="default"
								onClick={handlePrevious}
								disabled={currentPage === 1}
								className="gap-1 px-2.5 sm:pl-2.5 cursor-pointer"
								aria-label={t('actions.previous')}
							>
								<ChevronLeftIcon className="h-4 w-4" />
								<span className="hidden sm:block">{t('actions.previous')}</span>
							</Button>
						</PaginationItem>

						{pageNumbers.map((page, index) => {
							if (page === 'ellipsis') {
								return (
									<PaginationItem key={`ellipsis-${index}`}>
										<PaginationEllipsis />
									</PaginationItem>
								)
							}

							return (
								<PaginationItem key={page}>
									<PaginationLink
										onClick={(e) => handlePageClick(e, page)}
										isActive={page === currentPage}
										className="cursor-pointer border bg-shade text-primary"
									>
										{page}
									</PaginationLink>
								</PaginationItem>
							)
						})}

						<PaginationItem>
							<Button
								variant="ghost"
								size="default"
								onClick={handleNext}
								disabled={currentPage === totalPages}
								className="gap-1 px-2.5 sm:pr-2.5 cursor-pointer"
								aria-label={t('actions.next')}
							>
								<span className="hidden sm:block">{t('actions.next')}</span>
								<ChevronRightIcon className="h-4 w-4" />
							</Button>
						</PaginationItem>
					</PaginationContent>
				</Pagination>
			</div>
			<div>
				{pageSize && onPageSizeChange && (
					<Select
						value={String(pageSize)}
						onValueChange={(value) => onPageSizeChange(Number(value))}
					>
						<SelectTrigger className="rounded-full h-8 text-sm font-medium w-auto px-3 border-0">
							<SelectValue placeholder={String(pageSize)} />
						</SelectTrigger>
						<SelectContent>
							<SelectItem value="10">10</SelectItem>
							<SelectItem value="25">25</SelectItem>
							<SelectItem value="50">50</SelectItem>
							<SelectItem value="100">100</SelectItem>
						</SelectContent>
					</Select>
				)}
			</div>
		</div>
	)
}
